import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import safeToast from "utils/safeToast";
import axios from "axios";
import { API_ENDPOINTS } from "config/api";

// MDUI Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import { decryptText } from "config/encryption";

const WorkingDetails = () => {
  const [workingData, setWorkingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingWorking, setEditingWorking] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState({ Token: "", StudentId: 0, LoginId: 0 });

  const [form, setForm] = useState({
    workId: 0,
    company: "",
    designation: "",
    salary: "",
    address: "",
    country: "",
    district: "",
    location: "",
    fromDate: "",
    toDate: "",
    isCurrent: false,
  });

  const [dropdowns, setDropdowns] = useState({
    countries: [],
    districts: [],
    locations: [],
  });

  useEffect(() => {
    fetchWorkingDetails();
  }, []);

  useEffect(() => {
    if (form.district) {
      fetchLocationsByDistrict(form.district);
    } else {
      setDropdowns((prev) => ({ ...prev, locations: [] }));
    }
  }, [form.district, userInfo.Token]);

  const fetchWorkingDetails = async () => {
    try {
      const encryptedData = localStorage.getItem("UserLogDetails");
      if (!encryptedData) throw new Error("User session not found.");
      const decryptedData = decryptText(encryptedData);
      const parsed = JSON.parse(decryptedData);
      setUserInfo(parsed);

      // Use V2 API endpoint
      const workRes = await axios.get(API_ENDPOINTS.PROFILE.GET_WORKING_DETAILS_V2(parsed.StudentId), {
        headers: { Authorization: `Bearer ${parsed.Token}` },
      });

      setWorkingData(workRes.data || []);

      const dropdownRes = await axios.get(API_ENDPOINTS.PROFILE.GET_DROPDOWNS, {
        headers: { Authorization: `Bearer ${parsed.Token}` },
      });

      setDropdowns({
        countries: dropdownRes.data.countries || [],
        districts: dropdownRes.data.districts || [],
        locations: [],
      });
    } catch (error) {
      if (error.response?.status === 401) {
        safeToast.error("Session expired. Please log in again.");
        localStorage.clear();
        navigate("/BasicAuth");
      } else {
        safeToast.error("Failed to load working details.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationsByDistrict = async (districtId) => {
    try {
      const response = await axios.get(
        API_ENDPOINTS.PROFILE.GET_LOCATIONS_BY_DISTRICT(districtId),
        {
          headers: {
            Authorization: `Bearer ${userInfo.Token}`,
          },
        }
      );
      setDropdowns((prev) => ({ ...prev, locations: response.data || [] }));
    } catch (error) {
      console.error("Error fetching locations", error);
      safeToast.error("Failed to load locations.");
    }
  };

  const handleOpen = (work = null) => {
    if (work) {
      setEditingWorking(work);
      // Handle both Id (capital) and id (lowercase) from API
      const workId = work.Id !== undefined ? work.Id : (work.id !== undefined ? work.id : 0);
      
      // Convert date from dd/MM/yyyy to yyyy-MM-dd for date input
      const convertToInputDate = (dateStr) => {
        if (!dateStr) return "";
        if (dateStr.includes("/")) {
          const [dd, mm, yyyy] = dateStr.split("/");
          return `${yyyy}-${mm}-${dd}`;
        }
        return dateStr;
      };

      setForm({
        workId: workId,
        company: work.company || "",
        designation: work.designation || "",
        salary: work.salary || "",
        address: work.address || "",
        country: work.countryId || "",
        district: work.districtId || "",
        location: work.cityId || "",
        fromDate: convertToInputDate(work.startDate),
        toDate: convertToInputDate(work.endDate),
        isCurrent: work.isCurrent || false,
      });

      if (work.districtId) {
        fetchLocationsByDistrict(work.districtId);
      }
      setActiveStep(0);
    } else {
      setEditingWorking(null);
      setForm({
        workId: 0,
        company: "",
        designation: "",
        salary: "",
        address: "",
        country: "",
        district: "",
        location: "",
        fromDate: "",
        toDate: "",
        isCurrent: false,
      });
      setDropdowns((prev) => ({ ...prev, locations: [] }));
      setActiveStep(0);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingWorking(null);
    setActiveStep(0);
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (activeStep === 0) {
      // Step 1: Company & Designation
      if (!form.company || !form.designation) {
        safeToast.error("Please fill all required fields in Company Details");
        return;
      }
    } else if (activeStep === 1) {
      // Step 2: Location & Address
      if (!form.country || !form.district || !form.location || !form.address) {
        safeToast.error("Please fill all required fields in Location Details");
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const steps = [
    {
      label: "Company Details",
      description: "Enter company name, designation, and salary",
    },
    {
      label: "Location & Address",
      description: "Select country, district, location, and address",
    },
    {
      label: "Duration & Status",
      description: "Enter employment duration and current status",
    },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setForm((prev) => {
      if (name === "district") {
        fetchLocationsByDistrict(value);
        return { ...prev, [name]: value, location: "" };
      }
      if (name === "isCurrent" && checked) {
        return { ...prev, [name]: checked, toDate: "" };
      }
      return { ...prev, [name]: newValue };
    });
  };

  const handleSubmit = async () => {
    // Comprehensive validation
    const errors = [];

    if (!form.company) errors.push("Company Name");
    if (!form.designation) errors.push("Designation");
    if (!form.country) errors.push("Country");
    if (!form.district) errors.push("District");
    if (!form.location) errors.push("Location");
    if (!form.address) errors.push("Address");
    if (!form.fromDate) errors.push("From Date");

    // Validate date logic
    if (form.fromDate && form.toDate && !form.isCurrent) {
      const fromDate = new Date(form.fromDate);
      const toDate = new Date(form.toDate);
      if (toDate < fromDate) {
        errors.push("To Date cannot be before From Date");
      }
    }

    if (errors.length > 0) {
      safeToast.error(`Validation errors: ${errors.join(", ")}`);
      return;
    }

    // Convert date from yyyy-MM-dd to dd/MM/yyyy format
    const convertToBackendDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr.includes("-")) {
        const [yyyy, mm, dd] = dateStr.split("-");
        return `${dd}/${mm}/${yyyy}`;
      }
      return dateStr;
    };

    // Ensure we have the correct ID for updates
    let workingId = 0;
    if (form.workId !== undefined && form.workId !== null && form.workId !== 0) {
      workingId = parseInt(form.workId);
    } else if (editingWorking) {
      const workIdFromObj = editingWorking.Id !== undefined ? editingWorking.Id : editingWorking.id;
      if (workIdFromObj !== undefined && workIdFromObj !== null && workIdFromObj !== 0) {
        workingId = parseInt(workIdFromObj);
      }
    }

    const payload = {
      id: workingId !== 0 ? workingId : null,
      studentId: userInfo.StudentId,
      company: form.company,
      designation: form.designation,
      salary: form.salary ? parseFloat(form.salary) : null,
      countryId: form.country ? parseInt(form.country) : null,
      districtId: form.district ? parseInt(form.district) : null,
      cityId: form.location ? parseInt(form.location) : null,
      address: form.address || null,
      fromDate: convertToBackendDate(form.fromDate),
      toDate: form.isCurrent ? null : convertToBackendDate(form.toDate),
      isCurrent: form.isCurrent,
      createdUserId: userInfo.StudentId,
      loginId: userInfo.LoginId || 0,
    };

    try {
      let res;
      if (workingId !== 0 && workingId !== null) {
        // Update
        res = await axios.put(API_ENDPOINTS.PROFILE.UPDATE_WORKING_DETAIL_V2, payload, {
          headers: { Authorization: `Bearer ${userInfo.Token}` },
        });
      } else {
        // Insert
        res = await axios.post(API_ENDPOINTS.PROFILE.INSERT_WORKING_DETAIL_V2, payload, {
          headers: { Authorization: `Bearer ${userInfo.Token}` },
        });
      }

      const message = res.data?.message || res.data || "";

      if (message.includes("Error:") || message.includes("not found") || message.includes("already exists")) {
        safeToast.error(message);
      } else {
        safeToast.success(message);
        handleClose();
        fetchWorkingDetails();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        safeToast.error("Session expired. Please log in again.");
        localStorage.clear();
        navigate("/BasicAuth");
      } else {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to save working detail.";
        safeToast.error(errorMsg);
      }
    }
  };

  const formatDateRange = (from, to) => {
    if (!from && !to) return "Date not specified";
    if (!from) return `Until ${to}`;
    if (!to) return `Since ${from}`;
    return `${from} - ${to}`;
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" py={3}>
        <CircularProgress />
      </MDBox>
    );
  }

  return (
    <MDBox py={2} px={3}>
      <Card sx={{ boxShadow: 2, borderRadius: 2, bgcolor: "background.paper" }}>
        <MDBox
          p={2}
          mb={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderBottom: "1px solid", borderColor: "divider" }}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "text.secondary" }}>work</Icon>
            <MDTypography variant="h6" color="text" fontWeight="medium">
              Working Details
            </MDTypography>
            {workingData.length > 0 && (
              <Chip
                label={workingData.length}
                size="small"
                color="primary"
                sx={{
                  fontWeight: "medium",
                  height: "24px",
                }}
              />
            )}
          </MDBox>
          <Tooltip title="Add Working Detail">
            <MDButton
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => handleOpen()}
              startIcon={<Icon>add</Icon>}
            >
              Add
            </MDButton>
          </Tooltip>
        </MDBox>

        <CardContent sx={{ p: 3, bgcolor: "background.paper" }}>
          {workingData.length === 0 ? (
            <MDBox textAlign="center" py={6}>
              <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>work</Icon>
              <MDTypography variant="h6" color="text" fontWeight="medium" mb={1}>
                No Working Details
              </MDTypography>
              <MDButton variant="outlined" color="primary" onClick={() => handleOpen()}>
                Add Your First Work Experience
              </MDButton>
            </MDBox>
          ) : (
            <Grid container spacing={2}>
              {workingData.map((work) => {
                const workId = work.Id !== undefined ? work.Id : (work.id !== undefined ? work.id : null);
                const company = work.company || work.Company || "";
                const designation = work.designation || work.Designation || "";
                const salary = work.salary !== undefined ? work.salary : (work.Salary !== undefined ? work.Salary : null);
                const address = work.address || work.Address || "";
                const districtName = work.districtName || work.DistrictName || "";
                const locationName = work.locationName || work.LocationName || "";
                const startDate = work.startDate || work.StartDate || "";
                const endDate = work.endDate || work.EndDate || "";
                const experience = work.experience || work.Experience || "";
                const isCurrent = work.isCurrent !== undefined ? work.isCurrent : (work.IsCurrent !== undefined ? work.IsCurrent : false);
                const status = work.status || work.Status || (isCurrent ? "Current" : "Past");
                
                return (
                  <Grid item xs={12} key={workId}>
                    <Card sx={{ boxShadow: 1, borderRadius: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
                      <CardContent sx={{ p: 2, bgcolor: "background.paper" }}>
                        <MDBox display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpen(work)}
                              sx={{ color: "primary.main" }}
                            >
                              <Icon fontSize="small">edit</Icon>
                            </IconButton>
                          </Tooltip>
                        </MDBox>
                        <MDBox mb={1.5}>
                          <MDTypography variant="caption" color="success.main" mb={0.5}>
                            {company || "Company Name"}
                          </MDTypography>
                          <MDTypography variant="body2" color="text" mb={0.5}>
                            {designation || "Designation"}
                          </MDTypography>
                        </MDBox>
                        <Divider sx={{ my: 1 }} />
                        <MDBox>
                          {salary && (
                            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                              <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>attach_money</Icon>
                              <MDTypography variant="caption" color="text.secondary">
                                Salary:
                              </MDTypography>
                              <MDTypography variant="caption" fontWeight="bold" color="success.main">
                                ₹{typeof salary === 'number' ? salary.toLocaleString() : salary}
                              </MDTypography>
                            </MDBox>
                          )}
                          <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                            <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>calendar_today</Icon>
                            <MDTypography variant="caption" color="text.secondary">
                              Duration:
                            </MDTypography>
                            <MDTypography variant="caption" color="text" fontWeight="medium">
                              {formatDateRange(startDate, endDate)}
                            </MDTypography>
                          </MDBox>
                          {address && (
                            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                              <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>location_on</Icon>
                              <MDTypography variant="caption" color="text.secondary">
                                Address:
                              </MDTypography>
                              <MDTypography variant="caption" color="text" fontWeight="medium">
                                {address}
                              </MDTypography>
                            </MDBox>
                          )}
                          {districtName && (
                            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                              <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>place</Icon>
                              <MDTypography variant="caption" color="text.secondary">
                                District:
                              </MDTypography>
                              <MDTypography variant="caption" color="text" fontWeight="medium">
                                {districtName}
                              </MDTypography>
                            </MDBox>
                          )}
                          {locationName && (
                            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                              <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>map</Icon>
                              <MDTypography variant="caption" color="text.secondary">
                                Location:
                              </MDTypography>
                              <MDTypography variant="caption" color="text" fontWeight="medium">
                                {locationName}
                              </MDTypography>
                            </MDBox>
                          )}
                          {experience && (
                            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                              <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>schedule</Icon>
                              <MDTypography variant="caption" color="text.secondary">
                                Experience:
                              </MDTypography>
                              <MDTypography variant="caption" color="text" fontWeight="medium">
                                {experience}
                              </MDTypography>
                            </MDBox>
                          )}
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>check_circle</Icon>
                            <MDTypography variant="caption" color="text.secondary">
                              Status:
                            </MDTypography>
                            <Chip
                              label={status}
                              size="small"
                              color={isCurrent ? "success" : "default"}
                              sx={{ height: "20px", fontSize: "0.7rem" }}
                            />
                          </MDBox>
                        </MDBox>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Working Details Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
            bgcolor: "background.paper",
          }
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="medium">
              {editingWorking ? "Edit Working Detail" : "Add Working Detail"}
            </MDTypography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "text.secondary" }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: "75vh", overflowY: "auto", p: 3, bgcolor: "background.paper" }}>
          <Stepper
            activeStep={activeStep}
            orientation="vertical"
            sx={{
              bgcolor: "background.paper !important",
              background: "none !important",
              boxShadow: "none !important",
              padding: 0,
              "& .MuiStep-root": {
                bgcolor: "background.paper",
              },
              "& .MuiStepLabel-root": {
                bgcolor: "background.paper",
                "& .MuiStepLabel-label": {
                  color: "text.primary",
                  "&.Mui-active": {
                    color: "primary.main",
                  },
                  "&.Mui-completed": {
                    color: "text.primary",
                  },
                },
              },
              "& .MuiStepContent-root": {
                bgcolor: "background.paper",
                borderLeft: "2px solid",
                borderColor: "divider",
                marginLeft: "20px",
                paddingLeft: "24px",
              },
              "& .MuiStepConnector-root": {
                marginLeft: "20px",
                "& .MuiStepConnector-line": {
                  borderColor: "divider",
                  borderWidth: "2px",
                },
                "&.Mui-active .MuiStepConnector-line": {
                  borderColor: "divider",
                },
                "&.Mui-completed .MuiStepConnector-line": {
                  borderColor: "divider",
                },
              },
              "& .MuiStepIcon-root": {
                color: "divider",
                border: "2px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                "&.Mui-active": {
                  color: "primary.main",
                  borderColor: "primary.main",
                  bgcolor: "background.paper",
                },
                "&.Mui-completed": {
                  color: "primary.main",
                  borderColor: "primary.main",
                  bgcolor: "background.paper",
                },
              },
            }}
          >
            {/* Step 1: Company Details */}
            <Step>
              <StepLabel>
                <MDTypography variant="subtitle1" fontWeight="medium">
                  {steps[0].label}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  {steps[0].description}
                </MDTypography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Company Name *
                      </InputLabel>
                      <TextField
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Enter company name"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Designation *
                      </InputLabel>
                      <TextField
                        name="designation"
                        value={form.designation}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Enter your designation"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Salary (Optional)
                      </InputLabel>
                      <TextField
                        name="salary"
                        type="number"
                        value={form.salary}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Enter salary (e.g., 50000)"
                        helperText="Salary is optional"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
                      <MDButton variant="outlined" color="secondary" onClick={handleClose}>
                        Cancel
                      </MDButton>
                      <MDButton variant="contained" color="primary" onClick={handleNext}>
                        Next
                        <Icon sx={{ ml: 0.5 }}>arrow_forward</Icon>
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 2: Location & Address */}
            <Step>
              <StepLabel>
                <MDTypography variant="subtitle1" fontWeight="medium">
                  {steps[1].label}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  {steps[1].description}
                </MDTypography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Country *
                      </InputLabel>
                      <Autocomplete
                        options={dropdowns.countries}
                        getOptionLabel={(option) => option.txt || ""}
                        value={dropdowns.countries.find((o) => o.id === parseInt(form.country)) || null}
                        onChange={(event, newValue) => {
                          setForm((prev) => ({ ...prev, country: newValue?.id || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Country"
                            size="small"
                            fullWidth
                            variant="outlined"
                          />
                        )}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        District *
                      </InputLabel>
                      <Autocomplete
                        options={dropdowns.districts}
                        getOptionLabel={(option) => option.txt || ""}
                        value={dropdowns.districts.find((o) => o.id === parseInt(form.district)) || null}
                        onChange={(event, newValue) => {
                          handleChange({ target: { name: "district", value: newValue?.id || "" } });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select District"
                            size="small"
                            fullWidth
                            variant="outlined"
                          />
                        )}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Location *
                      </InputLabel>
                      <Autocomplete
                        options={dropdowns.locations}
                        getOptionLabel={(option) => option.txt || ""}
                        value={dropdowns.locations.find((o) => o.id === parseInt(form.location)) || null}
                        onChange={(event, newValue) => {
                          setForm((prev) => ({ ...prev, location: newValue?.id || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Location"
                            size="small"
                            fullWidth
                            variant="outlined"
                            disabled={!form.district}
                          />
                        )}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Address *
                      </InputLabel>
                      <TextField
                        name="address"
                        value={form.address}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        variant="outlined"
                        multiline
                        rows={3}
                        placeholder="Enter complete address"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" gap={1} mt={2}>
                      <MDButton variant="outlined" color="secondary" onClick={handleBack}>
                        <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                        Back
                      </MDButton>
                      <MDButton variant="contained" color="primary" onClick={handleNext}>
                        Next
                        <Icon sx={{ ml: 0.5 }}>arrow_forward</Icon>
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 3: Duration & Status */}
            <Step>
              <StepLabel>
                <MDTypography variant="subtitle1" fontWeight="medium">
                  {steps[2].label}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  {steps[2].description}
                </MDTypography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        From Date *
                      </InputLabel>
                      <TextField
                        name="fromDate"
                        type="date"
                        value={form.fromDate}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        helperText="Select the date when you started"
                      />
                    </MDBox>
                  </Grid>

                  {!form.isCurrent && (
                    <Grid item xs={12} sm={6}>
                      <MDBox mb={2}>
                        <InputLabel sx={{ mb: 1 }}>
                          To Date
                        </InputLabel>
                        <TextField
                          name="toDate"
                          type="date"
                          value={form.toDate}
                          onChange={handleChange}
                          fullWidth
                          size="small"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          helperText="Select the date when you left (leave blank if current)"
                        />
                      </MDBox>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isCurrent"
                            checked={form.isCurrent}
                            onChange={(e) => {
                              if (form.toDate) {
                                safeToast.error("Please clear To Date first if marking as current employer.");
                                return;
                              }
                              handleChange(e);
                            }}
                          />
                        }
                        label="Current Employer"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" gap={1} mt={2}>
                      <MDButton variant="outlined" color="secondary" onClick={handleBack}>
                        <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                        Back
                      </MDButton>
                      <MDButton
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        startIcon={<Icon>{editingWorking ? "save" : "check"}</Icon>}
                      >
                        {editingWorking ? "Update Working Detail" : "Save Working Detail"}
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
      </Dialog>
    </MDBox>
  );
};

export default WorkingDetails;
